import {
  CompositeFilterDescriptor,
  DataSourceRequestState,
  FilterDescriptor
} from '@progress/kendo-data-query';
import {
  DateRangePicker,
  DateRangePickerCalendarSettings,
  DateRangePickerChangeEvent,
  DateRangePickerDateInputSettings,
  SelectionRange
} from '@progress/kendo-react-dateinputs';
import { GridFilterCellProps } from '@progress/kendo-react-grid';
import { endOfDay } from 'date-fns';
import { useEffect, useState } from 'react';
import { toDate } from 'src/utils/date';
import styled from 'styled-components';

const FilterCell = styled.div`
  font-weight: 400;

  &&& {
    .k-daterangepicker-wrap {
      display: inline-flex !important;
      .k-input {
        font-size: 14px;
      }
    }

    .k-dateinput:first-child .k-dateinput-wrap:after {
      content: '–';
      display: inline-flex;
      align-items: center;
      margin: 0 4px 0 0;
      font-size: 14px;
    }
  }
`;

interface Props {
  requestState: DataSourceRequestState;
  onFilterChange: (filter: CompositeFilterDescriptor | undefined) => void;
}

export const DateRangeFilterCell = ({
  field,
  requestState,
  onFilterChange
}: Props & GridFilterCellProps) => {
  const [calendarSettings] = useState<DateRangePickerCalendarSettings>({
    views: 1
  });

  const startDateInputSettings: DateRangePickerDateInputSettings = {
    label: undefined,
    width: 88,
    placeholder: '',
    format: 'dd.MM.yyyy',
    formatPlaceholder: 'narrow'
  };
  const endDateInputSettings: DateRangePickerDateInputSettings = {
    label: undefined,
    width: 76,
    placeholder: '',
    format: 'dd.MM.yyyy',
    formatPlaceholder: 'narrow'
  };

  const [filter] = useState<CompositeFilterDescriptor | undefined>(
    requestState.filter
  );

  const [dateRange, setDateRange] = useState<SelectionRange>();

  const onDateChange = (event: DateRangePickerChangeEvent) => {
    const value = event.value;
    setDateRange(value);
    const filters =
      filter?.filters.filter(
        (f: CompositeFilterDescriptor | FilterDescriptor) =>
          (f as FilterDescriptor).field !== field
      ) ?? [];

    if (!!value.start && !!value.end) {
      filters.push({
        field: field,
        value: value.start,
        operator: 'gte'
      });
      filters.push({
        field: field,
        value: endOfDay(value.end),
        operator: 'lte'
      });

      onFilterChange({
        logic: filter?.logic ?? 'and',
        filters: filters
      });
    }
  };

  useEffect(() => {
    if (requestState.filter) {
      const [start, end] =
        requestState.filter?.filters
          .filter(f => (f as FilterDescriptor).field === field)
          .map(f => (f as FilterDescriptor).value) ?? [];

      if (!!start && !!end) {
        setDateRange({
          start: toDate(start),
          end: toDate(end)
        });
      }
    }
  }, [field, requestState.filter]);

  return (
    <FilterCell className="k-filtercell">
      <DateRangePicker
        value={dateRange}
        calendarSettings={calendarSettings}
        startDateInputSettings={startDateInputSettings}
        endDateInputSettings={endDateInputSettings}
        onChange={onDateChange}
      />
    </FilterCell>
  );
};
